import React, { useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Popover } from '@barges/react';
import { analyticsService } from '@common/analytics';
import classes from './classes.module.scss';
import { SUPPORTED_LANGUAGES, getCurrentLocalization } from '../../config/i18n';
import { log, LOG_LEVELS } from '../../../common/config/app_logger';
import { removeDataFromStorage } from '../../../common/config/utils';
import { SvgIcon } from '../../../common/sprites';
import { appRoutes } from '../../config/app_routes';
import AdvisorNotProvideServicesPopup from './advisor_not_provide_services_popup';
import { useCurrentPath } from '../routes/hooks/useCurrentPath';
import { useLocalizedPath } from '../routes/hooks/useLocalizedPath';
import { getAdvisorIdBySlug } from '../../project_items/helper_functions';
import { updateUserAPI, updateCurrencyAPI } from '../../../common/api/api';
import { getCurrentCurrencyCode, isMulticurrencyAllowed } from '../../config/util';
import { setApplicationError } from '../../actions/aplication_error';

function ChangeLanguage({
  show = false,
  onClose,
  advisors,
  dynamicConfig,
  onCurrencyChanged
}) {
  const SUPPORTED_CURRENCIES = dynamicConfig?.supportedCurrencies || [];
  const language = getCurrentLocalization();
  const currentCurrency = getCurrentCurrencyCode();
  const { t } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [selectedCurrency, setSelectedCurrency] = useState(currentCurrency);
  const [advisorNotProvideServicesPopupData, setAdvisorNotProvideServicesPopupData] = useState({});
  const currentPath = useCurrentPath();
  const generatePath = useLocalizedPath();
  const dispatch = useDispatch();

  // BroadcastChannel for communication with other tabs
  const sendBroadcastEvent = (currency) => {
    if (typeof BroadcastChannel === 'undefined') {
      log(LOG_LEVELS.INFO, 'BroadcastChannel', 'BroadcastChannel is not supported in this browser');
      window.location.reload();
    } else {
      const bc = new BroadcastChannel('currency_changed');
      bc.postMessage({ currency });
    }
  };

  useEffect(() => {
    if (show) {
      setSelectedLanguage(language);
      setSelectedCurrency(currentCurrency);
      analyticsService.trackPageVisit('language setting');
    }
  }, [show]);

  const onCloseClicked = useCallback(() => {
    onClose({ show: false });
  }, []);

  const makeChangeLanguage = ({ target, lang }) => {
    target.disabled = true;
    const redirecPath = generatePath(lang, currentPath.routeKey, currentPath.params, currentPath.search);
    log(LOG_LEVELS.INFO, 'General', `On popup language changed from ${ language } to ${ lang }`);
    log(LOG_LEVELS.INFO, 'General', `Redirect from  ${ window.location.pathname } to ${ redirecPath }`);
    removeDataFromStorage('advisor_urgency_signal');
    window.location.replace(redirecPath);
  };

  const onChangeCurrencyError = (error) => {
    error
      .json()
      .then(jsonError => {
        dispatch(setApplicationError({ errorText: jsonError.errors.currency[0] }));
      })
      .catch(e => {
        dispatch(setApplicationError({ errorText: e?.message }));
      });
  };

  const makeChangeCurrency = ({ target, currency }) => {
    target.disabled = true;
    if (window.currentUserId !== 0 && window.currentUserId !== undefined) {
      updateUserAPI({ currency }).then(() => {
        sendBroadcastEvent(currency);
        onCloseClicked();
        if (onCurrencyChanged) onCurrencyChanged();
      }).catch((error) => {
        log(LOG_LEVELS.ERROR, 'Change currency ', 'Error updating user currency', error);
        onChangeCurrencyError(error);
        onCloseClicked();
      });
    } else {
      updateCurrencyAPI(currency).then(() => {
        sendBroadcastEvent(currency);
        onCloseClicked();
        if (onCurrencyChanged) onCurrencyChanged();
      }).catch((error) => {
        log(LOG_LEVELS.ERROR, 'Change currency', 'Error updating currency', error);
        onChangeCurrencyError(error);
        onCloseClicked();
      });
    }
  };

  const onSave = useCallback((e) => {
    if (selectedCurrency && selectedCurrency !== currentCurrency) {
      makeChangeCurrency({ target: e.target, currency: selectedCurrency });
    }

    if (selectedLanguage === language) {
      onCloseClicked();
      return;
    }

    if (currentPath.routeKey !== appRoutes.advisor) {
      makeChangeLanguage({ target: e.target, lang: selectedLanguage });
      return;
    }

    const advisorId = getAdvisorIdBySlug({ advisorSlug: currentPath.params.slug });
    const advisorProfileLanguages = advisors[advisorId]?.profileLanguages;
    if (!advisorProfileLanguages.includes(selectedLanguage)) {
      setAdvisorNotProvideServicesPopupData({ show: true, lang: selectedLanguage });
      return;
    }

    makeChangeLanguage({ target: e.target, lang: selectedLanguage });
  }, [selectedLanguage, currentPath.routeKey, advisors, language, selectedCurrency]);

  const renderRadioButton = (isSelected) => (
    <div className={ classnames(
      classes.radioOuter,
      isSelected ? classes.radioSelected : classes.radioDefault
    ) }
    >
      {isSelected && <div className={ classes.radioInnerDot } />}
    </div>
  );

  const renderContent = () => (
    <div className={ classes.container }>
      <div className={ classes.titleContainer }>
        <span className={ classes.title }>{ isMulticurrencyAllowed() ? t('language.popup_lc_title') : t('language.popup_title') }</span>
      </div>

      <button type="button" onClick={ onCloseClicked } className={ classes.closeBtn }>
        <SvgIcon id="close_icon" size={ 18 } />
      </button>

      <div className={ classes.sectionsWrapper }>
        <div className={ classes.section }>
          <span className={ classes.sectionLabel }>{t('language.language_label')}</span>
          <div className={ classes.table }>
            {SUPPORTED_LANGUAGES.map(lang => (
              <button
                key={ lang }
                type="button"
                className={ classes.tableRow }
                onClick={ () => setSelectedLanguage(lang) }
              >
                <span className={ classes.rowLabel }>{t(`language.${ lang }`)}</span>
                {renderRadioButton(lang === selectedLanguage)}
              </button>
            ))}
          </div>
        </div>

        { dynamicConfig?.multicurrencyAllowed && dynamicConfig?.multicurrencyAllowed === true && (
          <div className={ classes.section }>
            <span className={ classes.sectionLabel }>{t('language.currency_label')}</span>
            <div className={ classes.table }>
              {SUPPORTED_CURRENCIES.map(currency => (
                <button
                  key={ currency.key }
                  type="button"
                  className={ classes.tableRow }
                  onClick={ () => setSelectedCurrency(currency.key) }
                >
                  <span className={ classes.rowLabel }>{currency.name}</span>
                  {renderRadioButton(currency.key === selectedCurrency)}
                </button>
              ))}
            </div>
          </div>
        ) }
      </div>

      <button type="button" className={ classes.saveButton } onClick={ onSave }>
        {t('language.save_changes')}
      </button>
    </div>
  );

  const renderChangeLanguage = () => {
    if (!show) return null;
    return (
      <>
        <Popover
          isOpen={ show && !advisorNotProvideServicesPopupData.show }
          onCloseButtonClick={ onCloseClicked }
          centered
          closeOnBackdropClick
          className={ classes.popupContainer }
        >
          {renderContent()}
        </Popover>
        <AdvisorNotProvideServicesPopup
          onCloseCliked={ onCloseClicked }
          advisorNotProvideServicesPopupData={ advisorNotProvideServicesPopupData }
          setAdvisorNotProvideServicesPopupData={ setAdvisorNotProvideServicesPopupData }
          makeChangeLanguage={ makeChangeLanguage }
        />
      </>
    );
  };

  return renderChangeLanguage();
}

export default ChangeLanguage;
